import React, { useState } from 'react';
import './ZoomDocumentation.css';
import HowToIntegrateZoom from './guides/HowToIntegrateZoom';
import HowToDisconnectZoom from './guides/HowToDisconnectZoom';

const ZoomDocumentation = () => {
    const [currentView, setCurrentView] = useState('main');

    if (currentView === 'integrate') {
        return <HowToIntegrateZoom onBack={() => setCurrentView('main')} />;
    }

    if (currentView === 'disconnect') {
        return <HowToDisconnectZoom onBack={() => setCurrentView('main')} />;
    }

    return (
        <div className="zoom-docs-page">
            <div className="zoom-docs-container">
                <div className="zoom-docs-header">
                    <h1>ZOOM INTEGRATION DOCUMENTATION</h1>
                    <p className="last-updated">Last Updated: December 2025</p>
                </div>

                <div className="zoom-docs-content">
                    <section className="docs-section intro-section">
                        <h2>Welcome to ClientBloom Zoom Integration</h2>
                        <p>This documentation provides comprehensive guides for adding, using, and removing the ClientBloom Zoom integration.</p>
                    </section>

                    <section className="docs-section">
                        <h2>📚 Documentation Sections</h2>
                        <div className="guides-grid">
                            <div className="guide-card" onClick={() => setCurrentView('integrate')}>
                                <div className="guide-icon">➕</div>
                                <h3>Adding the Application</h3>
                                <p>Step-by-step guide to add and authorize ClientBloom to access your Zoom account.</p>
                                <button className="guide-button">View Guide →</button>
                            </div>

                            <div className="guide-card" onClick={() => setCurrentView('disconnect')}>
                                <div className="guide-icon">🗑️</div>
                                <h3>Removing the Application</h3>
                                <p>Complete guide to remove the integration and understand data implications.</p>
                                <button className="guide-button">View Guide →</button>
                            </div>
                        </div>
                    </section>

                    <section className="docs-section">
                        <h2>⚙️ Using the Integration</h2>
                        
                        <div className="usage-section">
                            <h3>Core Features</h3>
                            <div className="quick-info">
                                <div className="info-card">
                                    <h4>📹 Automatic Call Sync</h4>
                                    <p><strong>Use Case:</strong> Automatically import Zoom recordings into ClientBloom for analysis.</p>
                                    <p><strong>Prerequisites:</strong></p>
                                    <ul>
                                        <li>Zoom cloud recording enabled</li>
                                        <li>Active ClientBloom integration</li>
                                        <li>Meetings with participants</li>
                                    </ul>
                                    <p><strong>How it works:</strong> Daily sync retrieves recordings from the last 24 hours and processes them automatically.</p>
                                </div>

                                <div className="info-card">
                                    <h4>🔍 Manual Transcript Search</h4>
                                    <p><strong>Use Case:</strong> Manually search and import Zoom transcripts from specific time periods.</p>
                                    <p><strong>Prerequisites:</strong></p>
                                    <ul>
                                        <li>Active Zoom integration</li>
                                        <li>Access to Admin Panel → Integrations</li>
                                        <li>Cloud recordings with transcriptions available</li>
                                    </ul>
                                    <p><strong>How to use:</strong></p>
                                    <ul>
                                        <li>Go to <strong>Admin Panel</strong> → <strong>Integrations</strong></li>
                                        <li>Click <strong>"Manage"</strong> on the Zoom card</li>
                                        <li>Choose <strong>"Automatic"</strong> (last 24h) or <strong>"Date Range"</strong></li>
                                        <li>For date range: Select start and end dates</li>
                                        <li>Click <strong>"Search Transcripts"</strong> to import</li>
                                    </ul>
                                    <p><strong>Note:</strong> Manual search is useful for importing historical recordings or specific date ranges that weren't automatically synced.</p>
                                </div>

                                <div className="info-card">
                                    <h4>📝 Transcription Processing</h4>
                                    <p><strong>Use Case:</strong> Convert Zoom transcriptions into actionable insights.</p>
                                    <p><strong>Prerequisites:</strong></p>
                                    <ul>
                                        <li>Zoom automatic transcription enabled</li>
                                        <li>Transcriptions generated in VTT format</li>
                                        <li>Meeting must be recorded</li>
                                    </ul>
                                    <p><strong>How it works:</strong> Transcriptions are downloaded and analyzed by AI to generate insights.</p>
                                </div>

                                <div className="info-card">
                                    <h4>🎯 Client Matching</h4>
                                    <p><strong>Use Case:</strong> Associate Zoom calls with specific clients in your workspace.</p>
                                    <p><strong>Prerequisites:</strong></p>
                                    <ul>
                                        <li>Clients created in ClientBloom</li>
                                        <li>Client emails match Zoom participant emails</li>
                                        <li>Participants must have valid email addresses</li>
                                    </ul>
                                    <p><strong>How it works:</strong> System matches participant emails with client database automatically.</p>
                                </div>

                                <div className="info-card">
                                    <h4>📊 AI Analysis & CRS Update</h4>
                                    <p><strong>Use Case:</strong> Generate insights and update Client Retention Score based on call content.</p>
                                    <p><strong>Prerequisites:</strong></p>
                                    <ul>
                                        <li>Successful transcription processing</li>
                                        <li>Client matched to call</li>
                                        <li>Valid transcription content</li>
                                    </ul>
                                    <p><strong>How it works:</strong> AI analyzes conversation sentiment, topics, and risks to update client health scores.</p>
                                </div>

                                <div className="info-card">
                                    <h4>🔍 Viewing & Managing Calls</h4>
                                    <p><strong>Use Case:</strong> Access and review synced calls with transcriptions and analysis.</p>
                                    <p><strong>Prerequisites:</strong></p>
                                    <ul>
                                        <li>At least one synced call</li>
                                        <li>Access to Calls section in ClientBloom</li>
                                    </ul>
                                    <p><strong>How to access:</strong></p>
                                    <ul>
                                        <li>Navigate to <strong>Calls</strong> section in sidebar</li>
                                        <li>Select any call to view details</li>
                                        <li>Review transcription, AI insights, and metadata</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="docs-section troubleshooting-section">
                        <h2>🔧 Troubleshooting</h2>
                        <div className="troubleshooting-box">
                            <div className="trouble-item">
                                <h4>❌ Authorization Fails</h4>
                                <p><strong>Solution:</strong> Verify you're using the correct Zoom account with administrator permissions. Clear browser cache and try again.</p>
                            </div>
                            <div className="trouble-item">
                                <h4>❌ Calls Don't Sync</h4>
                                <p><strong>Solution:</strong> Ensure cloud recordings are enabled in Zoom settings. Check that automatic transcriptions are turned on. Allow 24 hours for initial sync.</p>
                            </div>
                            <div className="trouble-item">
                                <h4>❌ Client Matching Issues</h4>
                                <p><strong>Solution:</strong> Verify participant emails in Zoom match client emails in ClientBloom exactly. Check for typos or formatting differences.</p>
                            </div>
                            <div className="trouble-item">
                                <h4>❌ Missing Transcriptions</h4>
                                <p><strong>Solution:</strong> Confirm Zoom generated the transcription. Only meetings with transcriptions will be fully processed.</p>
                            </div>
                            <div className="trouble-item">
                                <h4>❓ Need More Help?</h4>
                                <p><strong>Contact Support:</strong> Email <a href="mailto:hello@clientbloom.ai">hello@clientbloom.ai</a> with your Workspace Name, Zoom email, meeting date/time (UTC), and screenshots.</p>
                            </div>
                        </div>
                    </section>

                    <section className="docs-section">
                        <h2>⚠️ Important Information</h2>
                        <div className="quick-info">
                            <div className="info-card">
                                <h4>System Requirements</h4>
                                <ul>
                                    <li>Workspace admin role in ClientBloom</li>
                                    <li>Zoom Pro, Business, or Enterprise account</li>
                                    <li>Cloud recording feature enabled</li>
                                    <li>Automatic transcription enabled</li>
                                </ul>
                            </div>

                            <div className="info-card">
                                <h4>Limitations</h4>
                                <ul>
                                    <li>Only cloud recordings sync (not local)</li>
                                    <li>Transcriptions must be generated by Zoom</li>
                                    <li>Sync runs once every 24 hours</li>
                                    <li>Client matching requires exact email match</li>
                                </ul>
                            </div>

                            <div className="info-card">
                                <h4>Data & Privacy</h4>
                                <ul>
                                    <li>Only recording metadata and transcriptions are stored</li>
                                    <li>Video files are not stored in ClientBloom</li>
                                    <li>Data is encrypted at rest and in transit</li>
                                    <li>Historical data persists after disconnection</li>
                                </ul>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default ZoomDocumentation;
